import { Suspense, lazy, type ReactNode } from 'react';

const AnimatedIcon = lazy(() => import('./AnimatedIcon'));

/**
 * NamedIcon — slot-pattern wrapper around AnimatedIcon.
 *
 * Call sites pass a short `name` (resolved to `/icons/${name}.json`)
 * plus a Lucide fallback. If the Lottie file exists in
 * `apps/web/public/icons/`, AnimatedIcon plays it; if it doesn't,
 * the Lucide fallback renders. Dropping a new JSON file activates
 * the slot at the next page load — no code change required.
 *
 * Default policy (Phase 2 slot-map mechanical refactor):
 *   - loop defaults to TRUE — every slot animation loops
 *     continuously. Callers can override with `loop={false}` for
 *     one-shot surfaces (e.g. a celebratory burst on
 *     SessionCompletePage where a single play is the desired UX).
 *   - staggerDelayMs defers the FIRST play. Useful when multiple
 *     slot animations sit next to each other (bottom nav,
 *     Ways-to-Earn cards, Profile rows) — without a stagger they
 *     tick in lockstep, which reads mechanical. Pass index*150
 *     across a row group for an organic feel.
 *
 * Suspense fallback IS the Lucide icon — so during the lottie-react
 * chunk's first load, every slot reads exactly as if no JSON file
 * had been authored. No spinner, no layout shift.
 */
interface NamedIconProps {
  /** Filename slot. Resolves to `/icons/${name}.json`. */
  name: string;
  /** Lucide fallback shown while the Lottie chunk loads or when
   *  no `{name}.json` exists at the static path. Required — no
   *  silent-empty render state. */
  fallback: ReactNode;
  /** Square CSS pixels. Default 24 (matches AnimatedIcon's default). */
  size?: number;
  /** Plays once on mount. Default true. Combined with `loop=true`
   *  (the new default) this means "start playing on mount and keep
   *  looping." Set false only when the slot must wait for a tap. */
  playOnMount?: boolean;
  /** Replay on tap. Default true. Bubbles the click so row-level
   *  navigation handlers still receive it. */
  playOnTap?: boolean;
  /** Continuous loop via Lottie's native loop mode. Default true
   *  (Phase 2 policy change — was false in the foundation commit).
   *  Set false for one-shot celebratory animations. */
  loop?: boolean;
  /** Ms to defer the initial play on mount. Default 0 (immediate).
   *  Used to stagger neighboring icons so they don't tick in
   *  lockstep. After the first delayed play, looping (if enabled)
   *  resumes at its native cadence. */
  staggerDelayMs?: number;
  /** Manual loop interval — only relevant when `loop=false`. After
   *  the first play, replay every N ms. Most callers want native
   *  loop instead; this stays for periodic-pulse cases. */
  loopInterval?: number;
  /** Lottie playback speed multiplier, forwarded to AnimatedIcon.
   *  Default 1 (native speed). Values < 1 slow the animation. */
  speed?: number;
}

export function NamedIcon({
  name,
  fallback,
  size = 24,
  playOnMount = true,
  playOnTap = true,
  loop = true,
  staggerDelayMs = 0,
  loopInterval = 0,
  speed = 1,
}: NamedIconProps) {
  return (
    <Suspense fallback={fallback}>
      <AnimatedIcon
        src={`/icons/${name}.json`}
        fallback={fallback}
        size={size}
        playOnMount={playOnMount}
        playOnTap={playOnTap}
        loop={loop}
        // AnimatedIcon still uses the original prop name `playDelay`
        // for backward compat with the helpers in ProfilePage.tsx.
        // NamedIcon exposes it under the clearer `staggerDelayMs`
        // name — same semantic, better-named at the consumer surface.
        playDelay={staggerDelayMs}
        loopInterval={loopInterval}
        speed={speed}
      />
    </Suspense>
  );
}

export default NamedIcon;
